import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen, Layers, Cpu, Brain, Shield, Database, Sparkles } from 'lucide-react';

const sections = [
  {
    id: 'philosophy',
    icon: BookOpen,
    title: 'The Philosophy Behind the Table',
    color: '#00D4FF',
    content: `Just as Mendeleev's periodic table organized chemistry by revealing underlying atomic patterns, this AI Periodic Table organizes artificial intelligence by functional relationships and maturity levels.

**Why a periodic table?**

Chemistry's periodic table works because it reflects real structure—elements in the same column share properties because they have similar electron configurations. Our AI table aims for similar explanatory power: elements in the same block share functional roles, and their position indicates maturity level.

**Key Principles:**
- **Functional Grouping**: Elements that serve similar purposes belong together
- **Maturity Levels**: From established (Perceptrons) to speculative (Singularity)
- **Compositional**: Real systems combine multiple elements
- **Evolving**: The table will change as the field advances

The goal isn't perfect taxonomy—it's useful mental scaffolding for navigating a complex, rapidly evolving field.`,
  },
  {
    id: 'foundation',
    icon: Layers,
    title: 'Foundation Block Deep Dive',
    color: '#9B59B6',
    content: `The Foundation block contains the mathematical primitives that everything else builds upon. Without understanding these, you cannot truly understand any AI system.

**Core Elements:**

**Pr - Perceptron**: The original artificial neuron (Rosenblatt, 1958). A linear classifier that sparked the field. Simple but foundational.

**Bp - Backpropagation**: The algorithm that enables learning by propagating errors backward through networks. Without this, modern deep learning wouldn't exist.

**At - Attention**: The mechanism that allows models to focus on relevant parts of input. The heart of transformers and the key to modern NLP success.

**Em - Embeddings**: Dense vector representations that capture semantic meaning. The bridge between discrete symbols and continuous computation.

**Why Foundation Matters:**
Every time you use ChatGPT, DALL-E, or any AI system, these foundational elements are at work. Understanding them reveals why AI systems behave the way they do.`,
  },
  {
    id: 'architecture',
    icon: Cpu,
    title: 'Architecture Evolution',
    color: '#3498DB',
    content: `The Architecture block traces the evolution of neural network structures—different hypotheses about how to organize computation for learning.

**Historical to Modern:**

**Nn → Ff → Cn → Rn → Ls → Tf**
(Neural Network → Feedforward → CNN → RNN → LSTM → Transformer)

Each architecture solved problems its predecessors couldn't:
- **CNNs** (1989): Spatial hierarchies for images
- **LSTMs** (1997): Long-range dependencies in sequences
- **Transformers** (2017): Parallel processing with attention

**Emerging Architectures:**

**Df - Diffusion**: Generating by reversing noise—the backbone of Stable Diffusion and DALL-E 3.

**Mb - Mamba/SSM**: State space models offering O(n) scaling vs transformers' O(n²)—a potential paradigm shift for long contexts.

**Mo - Mixture of Experts**: Scaling parameters without proportional compute increase. Powers models like GPT-4.`,
  },
  {
    id: 'training',
    icon: Brain,
    title: 'The Modern Training Pipeline',
    color: '#E67E22',
    content: `Training is where AI systems acquire their capabilities. Modern LLM training follows a three-stage pipeline:

**Stage 1: Pre-training (Pt)**
- Massive unsupervised learning on internet-scale data
- Self-supervised objectives (next token prediction)
- Creates general knowledge and capabilities
- Most expensive phase (millions of dollars)

**Stage 2: Fine-tuning (Ft)**
- Supervised learning on curated examples
- Task-specific adaptation
- Instruction following training
- Hours to days, much cheaper

**Stage 3: Alignment (Rh, Dp)**
- RLHF: Learning from human preferences
- DPO: Direct preference optimization (simpler alternative)
- Makes models helpful, harmless, honest
- The "secret sauce" of ChatGPT-style assistants

**Key Insight:** Pre-training provides raw capability; alignment shapes how that capability is used.`,
  },
  {
    id: 'techniques',
    icon: Sparkles,
    title: 'Prompting & Agent Techniques',
    color: '#1ABC9C',
    content: `The Techniques block bridges theory and practice—these are the design patterns that turn raw models into useful systems.

**Prompting Hierarchy:**

**Zs - Zero-Shot**: Instructions only, no examples
**Fs - Few-Shot**: Learning from examples in the prompt
**Co - Chain-of-Thought**: "Think step by step" reasoning
**Ic - In-Context Learning**: Teaching through demonstration

**The Agent Stack:**

Modern agents combine multiple techniques:

\`\`\`
Ag (Agents) = Pl (Planning) + Tu (Tool Use) + Me (Memory) + Re (Reflection)
\`\`\`

**Critical Techniques:**

**Rg - RAG**: Retrieval-Augmented Generation. Fetching relevant context before generating. Reduces hallucination, enables knowledge updates.

**Mc - MCP**: Model Context Protocol. Standardized tool integration—the "USB for AI."

**Ma - Multi-Agent**: Multiple AI agents collaborating or debating. Enables complex workflows and self-correction.`,
  },
  {
    id: 'safety',
    icon: Shield,
    title: 'Safety & Alignment',
    color: '#E74C3C',
    content: `The Safety block exists because powerful technology requires proportional safety measures. This isn't paranoia—it's engineering prudence.

**Defensive Elements:**

**Gr - Guardrails**: Hard constraints on model behavior. Content filters, output validation, usage limits.

**Al - Alignment**: Training models to pursue human-intended goals. The core challenge of AI safety.

**Ca - Constitutional AI**: Training with explicit behavioral principles. Anthropic's approach to scalable alignment.

**Understanding Elements:**

**Ip - Interpretability**: Why did the model say that? Mechanistic understanding of neural networks.

**Xp - Explainability**: Communicating model decisions to users in understandable terms.

**Threat Elements:**

**Hl - Hallucination**: (Anti-element) Generating false information confidently. The reliability challenge.

**Se - Security**: Protection against jailbreaks, prompt injection, and adversarial attacks.

**The Core Insight:** Safety isn't a feature to add later—it should be integrated from the beginning.`,
  },
  {
    id: 'data',
    icon: Database,
    title: 'Data: The Hidden Driver',
    color: '#795548',
    content: `Models are only as good as their data. This truth is often forgotten in architecture obsession.

**The Data Pipeline:**

\`\`\`
Raw Sources → Cleaning (Cl) → Curation (Cu) → Annotation (An) → Training Data (Td)
                    ↑
            Augmentation (Au)
                    ↑
          Synthetic Data (Sy)
\`\`\`

**Key Data Elements:**

**Sy - Synthetic Data**: AI-generated data for training AI. Increasingly important as real data becomes scarce.

**Kg - Knowledge Graphs**: Structured entity relationships. Enables reasoning over explicit facts.

**Md - Multimodal Data**: Aligned data across text, image, audio, video. The foundation of multimodal models.

**Data Types by Modality:**
- **Tx**: Text (Common Crawl, books, code)
- **Ig**: Images (ImageNet, LAION)
- **Vd**: Video (Kinetics, YouTube)
- **Ad**: Audio (LibriSpeech, Common Voice)

**The Quality Principle:** A smaller, high-quality dataset often outperforms a larger, noisy one.`,
  },
];

export default function ContentSections() {
  const [openSections, setOpenSections] = useState(['philosophy']);

  const toggleSection = (id) => {
    setOpenSections(prev =>
      prev.includes(id)
        ? prev.filter(s => s !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          Learn the <span className="text-gradient">Framework</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Deep dive into each block and understand how AI systems work.
          Click any section to expand.
        </p>
      </motion.div>

      {/* Accordion Sections */}
      <div className="space-y-4">
        {sections.map((section, index) => {
          const Icon = section.icon;
          const isOpen = openSections.includes(section.id);

          return (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="rounded-xl border border-white/10 overflow-hidden"
              style={{
                background: isOpen
                  ? `linear-gradient(135deg, ${section.color}10 0%, transparent 100%)`
                  : 'transparent',
              }}
            >
              {/* Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center gap-4 hover:bg-white/5 transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${section.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: section.color }} />
                </div>
                <span className="font-semibold flex-1 text-left">{section.title}</span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-500 transition-transform ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Content */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div
                        className="prose prose-invert prose-sm max-w-none"
                        style={{
                          '--tw-prose-headings': 'white',
                          '--tw-prose-body': '#9ca3af',
                          '--tw-prose-bold': 'white',
                          '--tw-prose-code': section.color,
                        }}
                      >
                        {section.content.split('\n\n').map((paragraph, i) => {
                          if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                            return (
                              <h4 key={i} className="text-white font-semibold mt-4 mb-2">
                                {paragraph.replace(/\*\*/g, '')}
                              </h4>
                            );
                          }
                          if (paragraph.startsWith('```')) {
                            const code = paragraph.replace(/```/g, '').trim();
                            return (
                              <pre
                                key={i}
                                className="bg-dark-800 rounded-lg p-4 text-sm overflow-x-auto my-4"
                              >
                                <code style={{ color: section.color }}>{code}</code>
                              </pre>
                            );
                          }
                          return (
                            <p
                              key={i}
                              className="text-gray-400 leading-relaxed mb-3"
                              dangerouslySetInnerHTML={{
                                __html: paragraph
                                  .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>')
                                  .replace(/`([^`]+)`/g, `<code class="px-1.5 py-0.5 rounded bg-dark-800" style="color: ${section.color}">$1</code>`),
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Expand All / Collapse All */}
      <div className="mt-6 flex justify-center gap-4">
        <button
          onClick={() => setOpenSections(sections.map(s => s.id))}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          Expand All
        </button>
        <span className="text-gray-600">|</span>
        <button
          onClick={() => setOpenSections([])}
          className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          Collapse All
        </button>
      </div>
    </div>
  );
}
